import React, { useMemo, useEffect, useRef, useState, useCallback, useContext } from 'react';
import { useWidgetContext } from '../contexts/WidgetContext'; 
const CitationWidget: React.FC = () => {
    const { position, updatePosition } = useWidgetContext();
   
    // console.log("userData in widget:", userData);
    const [isOpen, setIsOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    
    // We only need the ref for the Icon/Base, not the whole list, for drag boundaries
    const iconRef = useRef<HTMLDivElement>(null);

    // --- Env Logic ---
    const widgetsFromEnv = useMemo(() => {
        const raw = (process.env.REACT_APP_WIDGETS as string | undefined) || (process.env as any).WIDGETS;
        if (!raw) return {} as Record<string, string>;
        try { return JSON.parse(raw) as Record<string, string>; } catch { return {}; }
    }, []);
    const widgetKeys = Object.keys(widgetsFromEnv);

    // --- Drag Logic ---
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        dragOffset.current = { x: event.clientX - position.x, y: event.clientY - position.y };
        setIsDragging(true);
    }, [position]);

    useEffect(() => {
        if (!isDragging) return;
        const handlePointerMove = (event: PointerEvent) => {
            // We clamp based on the ICON size (60px), not the list size.
            // This ensures the Icon stays on screen. The list might go off-top if at y=0.
            const width = 60; 
            const height = 60;
            const maxX = Math.max((window.innerWidth ?? 0) - width, 0);
            const maxY = Math.max((window.innerHeight ?? 0) - height, 0);
            
            updatePosition({
                x: clamp(event.clientX - dragOffset.current.x, 0, maxX),
                y: clamp(event.clientY - dragOffset.current.y, 0, maxY),
            });
        };
        const stopDragging = () => setIsDragging(false);
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
        };
    }, [isDragging, updatePosition]);

    // const handleMouseEnter = () => !isDragging && setIsOpen(true);
    // const handleMouseLeave = () => !isDragging && setIsOpen(false);
    const handleClick = () => { if (!isDragging) setIsOpen(prev => !prev); };

    if (!widgetKeys.length) return null;

    // --- Styles ---
    
    // 1. The Container is now strictly the size of the Master Icon (60x60)
    // It does not grow.
    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 9999,
        width: '60px',
        height: '60px',
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'top 0.1s ease, left 0.1s ease',
        userSelect: 'none',
        touchAction: 'none',
    };

    // 2. The Master Icon Styling
    const iconStyle: React.CSSProperties = {
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.65)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#4a5568',
        position: 'relative',
        zIndex: 10, // Higher than the list so it sits on top if needed
    };

    // 3. The List Styling (Positioned ABSOLUTE relative to the container)
    // bottom: '70px' means it starts 10px ABOVE the 60px container
    const listStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: '70px', 
        left: '0',
        width: '60px', // Match container width
        display: 'flex',
        flexDirection: 'column-reverse', // Stack items bottom-to-top
        alignItems: 'center',
        gap: '12px',
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)', 
        transformOrigin: 'bottom center', // Animates "growing" from the icon
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy pop-up effect
        paddingBottom: '10px' // Extra buffer
    };

    return (
        <div 
            ref={iconRef}
            style={containerStyle}
            onPointerDown={handlePointerDown}
            // onMouseEnter={handleMouseEnter}
            // onMouseLeave={handleMouseLeave}
        >
            {/* The Floating List (Appears Above) */}
            <div style={listStyle}>
                {widgetKeys.map((widget) => (
                    <a
                        key={widget}
                        href={widgetsFromEnv[widget]}
                        target="_blank"
                        rel="noopener noreferrer"
                        draggable={false}
                        style={{ 
                            transition: 'transform 0.2s',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <div style={{
                             width: '50px',
                             height: '50px',
                             borderRadius: '50%',
                             background: 'rgba(255, 255, 255, 0.8)',
                             backdropFilter: 'blur(12px)',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                             <img
                                src={`/img/${widget}.png`}
                                alt={widget}
                                width={35}
                                height={35}
                                style={{ borderRadius: '50%', objectFit: 'cover' }}
                            />
                        </div>
                    </a>
                ))}
            </div>

            {/* The Master Icon (Fixed Base) */}
            <div 
                onClick={handleClick}
                style={iconStyle}
            >
                {/* Inner colored circle */}
                <div style={{
                     width: '44px', 
                     height: '44px', 
                     borderRadius: '50%', 
                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: 'white',
                     transition: 'transform 0.3s ease',
                     transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)'
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default CitationWidget;